/**
 * auditLocalNotification.ts — Traduce un registro de auditoría en una notificación
 * real del sistema operativo (Android/iOS/Escritorio/Web) sin backend ni DB.
 *
 * QUÉ HACE:
 * - Reutiliza los mismos textos que ve el dueño en la tabla de Auditoría
 *   (getActionLabel para el título, resumen/formatDetailsSummary para el cuerpo)
 *   para que la notificación diga exactamente lo mismo que el log.
 * - Mapea `entidad` (módulo del log) → categoría de notificación y `severity`
 *   → prioridad, respetando las preferencias del usuario en Ajustes.
 *
 * QUÉ INVOCA: buildCategoryNotification + sendMobileNotification (lib/push),
 *             isNotificationCategoryEnabled (store), getActionLabel/formatDetailsSummary
 *             (mismo feature, ../utils/formatLogDetails).
 * QUÉ EXPORTA: notifyAuditEvent
 *
 * QUÉ NO DEBE HACER:
 * - No debe lanzar ni propagar errores: es best-effort, nunca debe romper el
 *   registro de auditoría que la origina.
 * - No debe duplicar los textos de las acciones (reutiliza formatLogDetails).
 */
import {
  buildCategoryNotification,
  sendMobileNotification,
  type NotificationCategory,
  type NotificationPriority,
} from "@/lib/push";
import {
  isNotificationCategoryEnabled,
  type SettingsNotificationCategory,
} from "@/store/notificationPrefsStore";
import { getActionLabel, formatDetailsSummary } from "./formatLogDetails";

/** Mapea el módulo (`entidad`) del log a la categoría de notificación configurable. */
const ENTIDAD_TO_CATEGORY: Record<string, SettingsNotificationCategory> = {
  caja: "ventas",
  terminales: "ventas",
  turnos: "ventas",
  facturacion: "ventas",
  tesoreria: "ventas",
  ventas: "ventas",
  margenes: "ventas",
  fidelizacion: "ventas",
  creditos: "ventas",
  inventario: "inventario",
  personal: "personal",
  personal_vacantes: "personal",
  personal_tareas: "personal",
  personal_asistencia: "personal",
  personal_turnos: "personal",
  personal_buzon: "personal",
  nomina: "personal",
};

/** Severidad del log → prioridad de la notificación (impacta sonido/insistencia). */
const SEVERITY_TO_PRIORITY: Record<string, NotificationPriority> = {
  low: "default",
  medium: "high",
  high: "max",
};

export interface AuditNotificationInput {
  action: string;
  entidad: string;
  severity?: string;
  resumen?: string;
  details?: Record<string, unknown>;
}

/**
 * Envía (best-effort) una notificación local del SO para un evento de auditoría,
 * solo si el usuario tiene activada la categoría correspondiente en Ajustes.
 * Cada evento se muestra INDIVIDUAL y persiste hasta que el usuario la toque
 * o la descarte (no se agrupan ni se resumen). `logId` (si se conoce) permite
 * que al tocar la notificación se abra directamente el detalle de ese
 * registro en Auditoría.
 */
export function notifyAuditEvent(
  entry: AuditNotificationInput,
  logId?: string,
): void {
  const category: NotificationCategory =
    ENTIDAD_TO_CATEGORY[entry.entidad] ?? "sistema";

  if (
    !isNotificationCategoryEnabled(category as SettingsNotificationCategory)
  ) {
    return;
  }

  const priority = SEVERITY_TO_PRIORITY[entry.severity ?? "low"] ?? "default";
  const title = getActionLabel(entry.action);
  const body =
    entry.resumen || formatDetailsSummary(entry.action, entry.details || {});

  const payload = buildCategoryNotification(
    category,
    title,
    body,
    logId ? { logId, screen: "auditoria" } : undefined,
    priority,
  );

  // Best-effort: una notificación fallida NUNCA debe romper el registro de
  // auditoría. sendMobileNotification ya atrapa sus propios errores; este
  // catch es una red de seguridad adicional silenciosa (misma convención que
  // los adaptadores de @/lib/platform).
  sendMobileNotification(payload).catch(() => {
    /* no-op */
  });
}
